<script module>
	export interface PricingCtaSnippet {
		pricingPackage: PricingPlanRecord
	}
</script>

<script lang="ts">
	import Icon from '../../components/icon/Icon.svelte'
	import Image from '../../components/image/ImageCms.svelte'
	import StructuredText from '../../components/structuredText/StructuredText.svelte'
	import StructuredTextBlocks from '../../components/structuredText/StructuredTextBlocks.svelte'
	import { isStructuredTextEmpty } from '../../components/structuredText/structuredText'
	import Tooltip from '../../components/tooltip/Tooltip.svelte'
	import { formatCurrency } from '../../domain/pricing/formatCurrency'
	import PricingBestValue from './PricingBestValue.svelte'
	import PricingCustomPlan from './PricingCustomPlan.svelte'
	import { defaultMonthlyRevenue } from './defaultMonthlyRevenue'
	import { getMonthlyPriceByRevenue, getPriceData } from './getMonthlyPriceByRevenue'
	import { MembershipPlanId } from './membershipPlanId'
	import { getPricingContext } from './pricingContext'
	import { pricingFeaturesComponents } from './pricingFeaturesComponents'
	import { MembershipsV1BillingCycle, MembershipsV1Currency } from '@sdk/Api'
	import { InformationCircle, ShieldCheck } from '@steeze-ui/heroicons'
	import type { PricingPlanRecord } from 'datocms/models'
	import { type Snippet, createEventDispatcher } from 'svelte'
	import { fly } from 'svelte/transition'

	interface Props {
		pricingPackage: PricingPlanRecord
		billingCycle: MembershipsV1BillingCycle
		currency: MembershipsV1Currency
		hasGroupBoost: boolean
		cta?: Snippet<[PricingCtaSnippet]>
	}

	let { pricingPackage, billingCycle, currency, hasGroupBoost, cta }: Props = $props()

	const { pricingTable, inApp, hideCreditCardInfo, hideCompareFeatures } = getPricingContext()

	let priceData = $derived(
		getPriceData(defaultMonthlyRevenue, pricingTable, pricingPackage.membershipPlanId),
	)

	let standardPrice = $derived(
		getMonthlyPriceByRevenue(
			defaultMonthlyRevenue,
			pricingTable,
			pricingPackage.membershipPlanId,
			currency,
			billingCycle,
		),
	)

	const dispatch = createEventDispatcher()

	function handleEstimatePrice() {
		dispatch('estimatePrice', { pricingPackage, billingCycle })
	}

	let isNetworkPlan = $derived(
		pricingPackage.membershipPlanId === MembershipPlanId.NETWORK ||
			pricingPackage.membershipPlanId === MembershipPlanId.NETWORK_PLUS,
	)

	let planTextColor = $derived(
		pricingPackage.membershipPlanId === MembershipPlanId.FREEMIUM
			? 'text-muted'
			: isNetworkPlan
				? 'text-orange-trackdesk'
				: 'text-green-trackdesk',
	)

	let hasBoostText = $derived(
		billingCycle === MembershipsV1BillingCycle.BILLING_CYCLE_MONTHLY
			? pricingPackage.boostTextMonthly && !isStructuredTextEmpty(pricingPackage.boostTextMonthly)
			: pricingPackage.boostTextAnnual && !isStructuredTextEmpty(pricingPackage.boostTextAnnual),
	)

	let boostLink = $derived(
		billingCycle === MembershipsV1BillingCycle.BILLING_CYCLE_MONTHLY
			? pricingPackage.boostLinkMonthly
			: pricingPackage.boostLinkAnnual,
	)

	let boostText = $derived(
		billingCycle === MembershipsV1BillingCycle.BILLING_CYCLE_MONTHLY
			? pricingPackage.boostTextMonthly
			: pricingPackage.boostTextAnnual,
	)
</script>

{#if hasGroupBoost}
	<svelte:element
		this={boostLink ? 'a' : 'div'}
		class="
		font-display flex h-[64px] items-center justify-center rounded-t-xl py-6 text-center leading-4
		{boostLink ? 'underline hover:no-underline' : ''}
		{hasBoostText ? '' : 'invisible'}
		{isNetworkPlan
			? 'bg-orange-trackdesk'
			: pricingPackage.membershipPlanId === MembershipPlanId.CUSTOM
				? 'bg-blue-trackdesk'
				: 'bg-trackdesk-green text-white'}
		"
		href={boostLink ?? null}
	>
		{#if boostText}
			<StructuredText structuredText={boostText.value} />
		{/if}
	</svelte:element>
{/if}

<div
	class="
		dark:text-body shadow-basic-new relative flex flex-1 flex-col items-stretch gap-7.5 rounded-b-xl border border-gray-200 bg-white px-7.5 py-7.5 text-center
		{hasGroupBoost && hasBoostText ? 'rounded-t-none border-t-transparent' : 'rounded-t-xl'}
	"
>
	{#if pricingPackage.customContent.length > 0}
		<PricingCustomPlan customContent={pricingPackage.customContent} />
	{:else}
		<div
			class="font-display text-dark text-2xl {isNetworkPlan
				? '[&_strong]:text-orange-trackdesk'
				: '[&_strong]:text-green-trackdesk'}"
		>
			<StructuredText structuredText={pricingPackage.nameFormatted.value} />
		</div>

		<div class="relative">
			{#if pricingPackage.membershipPlanId === MembershipPlanId.ENTERPRISE}
				<div class="relative z-10" in:fly={{ duration: 400, delay: 400, y: 20 }}>
					<PricingBestValue isGroupActive />
				</div>
			{/if}

			<div
				class="
				font-display h-[45px] text-5xl
				{planTextColor}
			"
			>
				{#if standardPrice}
					<span class="mr-2 text-sm"
						>{#if !inApp && !pricingPackage.hideFrom}from{/if}</span
					>{standardPrice}<span class="ml-2 text-sm">/mo</span>
				{:else if currency === MembershipsV1Currency.CURRENCY_USD}
					{pricingPackage.customPriceTitle}
				{:else if currency === MembershipsV1Currency.CURRENCY_EUR}
					{pricingPackage.customPriceTitleEur}
				{/if}
			</div>
		</div>

		{#if !inApp}
			<div class="flex items-center justify-center gap-1">
				<button
					name="estimate-price"
					onclick={handleEstimatePrice}
					class="
			text-dark underline hover:no-underline
			{pricingPackage.membershipPlanId === MembershipPlanId.FREEMIUM ||
					pricingPackage.hidePriceComparisonModal
						? 'invisible'
						: ''}
		"
				>
					{pricingPackage.priceComparisonModalTitle || 'Estimate your price'}
				</button>
				{#if pricingPackage.priceComparisonModalTooltip && !isStructuredTextEmpty(pricingPackage.priceComparisonModalTooltip)}
					<Tooltip>
						{#snippet placeholder()}
							<Icon small outline src={InformationCircle} />
						{/snippet}
						<div class="[&_a]:underline [&_a]:hover:no-underline">
							<StructuredText structuredText={pricingPackage.priceComparisonModalTooltip.value} />
						</div>
					</Tooltip>
				{/if}
			</div>
		{/if}

		<div class="-mx-2 flex flex-col items-center gap-2.5">
			{@render cta?.({ pricingPackage })}

			{#if !hideCreditCardInfo && !pricingPackage.hideCreditCardBadge}
				<div class="text-muted flex items-center justify-center gap-1 text-sm">
					<Icon src={ShieldCheck} class={planTextColor} small />
					No Credit Card Required
				</div>
			{/if}
		</div>

		<div
			class="[&_h4]:font-display flex flex-1 flex-col gap-5 text-left [&_a]:underline [&_a]:hover:no-underline [&_h4]:text-lg [&_h4]:font-normal"
		>
			{#each pricingPackage.mainFeatures as feature}
				<div
					class="{isNetworkPlan
						? '[&_h4_strong]:text-orange-trackdesk'
						: '[&_h4_strong]:text-green-trackdesk'} relative grid grid-cols-1 gap-4 rounded-md border border-dashed border-gray-200
						p-4
						nth-3:mt-auto
						{feature.large ? '[&_strong]:text-green-trackdesk [&_strong]:font-display text-lg' : ''}
						{feature.center ? 'text-center' : ''}
						"
				>
					{#if feature.icon}
						<div class="absolute top-0 left-0 -mt-[9px] flex w-full items-center justify-center">
							<div class="flex bg-white">
								<Image image={feature.icon} />
							</div>
						</div>
					{/if}

					{#if feature.description}
						<StructuredTextBlocks
							structuredText={feature.description}
							variables={{
								defaultMonthlyRevenue: priceData?.revenueCapTopInterval
									? formatCurrency(priceData.revenueCapTopInterval, true)
									: 'custom',
							}}
							components={pricingFeaturesComponents}
						/>
					{/if}
				</div>
			{/each}
		</div>

		{#if !pricingPackage.hideBottomCtas}
			<div class="-mx-2 mt-auto flex flex-col-reverse items-center gap-2.5">
				{@render cta?.({ pricingPackage })}
			</div>

			{#if !hideCompareFeatures}
				<a class="text-muted underline hover:no-underline" href="#features">
					Compare All Features
				</a>
			{/if}
		{/if}
	{/if}
</div>
